import React, { useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select as ChakraSelect,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { createSupportTicket } from "../../../../api/user-service";

interface SupportTicketModalProps {
  isOpen: boolean; // Controls whether the modal is open
  onClose: () => void; // Callback for closing the modal
}

const SupportTicketModal: React.FC<SupportTicketModalProps> = ({ isOpen, onClose }) => {
  const [ticketName, setTicketName] = useState<string>(''); // Ticket Name state
  const [description, setDescription] = useState<string>(''); // Description state
  const [product, setProduct] = useState<string>(''); // Dropdown state
  const [errorFlag, setErrorFlag] = useState(false); // Validation flag
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();
  const textColorSecondary = 'gray.500'; // Secondary text color

  const handleSubmit = () => {
    if (!ticketName || !description || !product) {
      // Form validation: Ensure all fields are filled in
      setErrorFlag(true);
      return;
    }
    setErrorFlag(false);

    setIsSubmitting(true);

    const ticketData = {
      ticketName,
      description,
      product,
    };

    createSupportTicket(ticketData)
      .then(() => {
        toast({
          title: 'Ticket Submitted!',
          description: 'Your support ticket has been submitted successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        handleClose();
      })
      .catch((error) => {
        // Show error toast notification
        toast({
          title: 'Submission Failed',
          description: 'There was an issue submitting your support ticket. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error('Error submitting ticket:', error);
      })
      .finally(() => {
        setIsSubmitting(false);
      })
    ;
  };

  const handleClose = () => {
    setTicketName('');
    setDescription('');
    setProduct('');
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size='2xl'>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Submit a Support Ticket</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <Text mb="12px" color={textColorSecondary} fontSize="md">
            Please fill out the form below to submit a support request.
          </Text>

          {/* Ticket Name Field */}
          <FormControl isRequired mb={4}>
            <FormLabel>Ticket Name</FormLabel>
            <Input
              value={ticketName}
              type="text"
              placeholder="Enter a descriptive ticket name"
              onChange={(e) => setTicketName(e.target.value)}
            />
            {errorFlag && !ticketName && (
              <Text fontSize="sm" color="red.500" mt={2}>
                Ticket name is required.
              </Text>
            )}
          </FormControl>

          {/* Description Field */}
          <FormControl isRequired mb={4}>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={description}
              placeholder="Describe your issue or request..."
              onChange={(e) => setDescription(e.target.value)}
            />
            {errorFlag && !description && (
              <Text fontSize="sm" color="red.500" mt={2}>
                Description is required.
              </Text>
            )}
          </FormControl>

          {/* Product Dropdown */}
          <FormControl isRequired>
            <FormLabel>Product</FormLabel>
            <ChakraSelect
              placeholder="Select a product"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            >
              <option value="SmartTax">SmartTax</option>
              <option value="Training">Training</option>
              <option value="SmartPortfolio">SmartPortfolio</option>
            </ChakraSelect>
            {errorFlag && !product && (
              <Text fontSize="sm" color="red.500" mt={2}>
                Product selection is required
              </Text>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="brandScheme"
            mr={3}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            Submit
          </Button>
          <Button colorScheme="brandScheme" variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SupportTicketModal;